import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { cn } from '@repo/ui/lib/utils';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * "Always Watching" — a scroll-pinned Three.js + canvas experience.
 *
 * Ported from the standalone landinghtml/agents-section.html prototype.
 * Adaptations for the landing page:
 *  - Light-only palette (no dark-mode toggle).
 *  - Scroll progress is derived from the section's own bounding rect instead of
 *    window.scrollY, so it works while the page scrolls inside the
 *    `.landing-scroll-container` element rather than the window.
 *  - The comet intro fires the first time the section scrolls into view.
 *
 * The bulk of the logic is intentionally imperative (WebGL + 2D canvas particle
 * system) and lives inside a single mount effect operating on scoped refs.
 */

type Agent = {
  name: string;
  code: string;
  align: 'left' | 'right';
  title: string;
  caption: string;
  signals: string[];
  icon: keyof typeof ICONS;
};

const AGENTS: Agent[] = [
  {
    name: 'Raven Pulse',
    code: 'Agent RCB05',
    align: 'left',
    get title() {
      return t('landing.detectingSocialSignals');
    },
    get caption() {
      return t('copy.socialCaption');
    },
    get signals() {
      return [
        t('extra.negativeSentimentSpikes'),
        t('extra.influencerMentionsSurge'),
        t('extra.hidingAffiliationDetected'),
      ];
    },
    icon: 'chart',
  },
  {
    name: 'Raven Atlas',
    code: 'Agent RCB03',
    align: 'right',
    get title() {
      return t('landing.assessingJurisdictionRisk');
    },
    get caption() {
      return t('copy.countryCaption');
    },
    get signals() {
      return [
        t('copy.fatfGreyList'),
        t('extra.weakAmlEnforcement'),
        t('extra.highCorruptionIndex'),
      ];
    },
    icon: 'globe',
  },
  {
    name: 'Raven Sentinel',
    code: 'Agent RCB01',
    align: 'left',
    get title() {
      return t('landing.scanningForAdverseMedia');
    },
    get caption() {
      return t('copy.mediaCaption');
    },
    get signals() {
      return [
        t('extra.fraudAllegationsFound'),
        t('extra.courtCaseMentioned'),
        t('extra.whistleblowerReportPublished'),
      ];
    },
    icon: 'search',
  },
  {
    name: 'Raven Dignitary',
    code: 'Agent RCB02',
    align: 'right',
    get title() {
      return t('landing.checkingPepExposure');
    },
    get caption() {
      return t('copy.politicalCaption');
    },
    get signals() {
      return [t('extra.relativeIsPep'), t('extra.businessPartnerPolitician')];
    },
    icon: 'user',
  },
  {
    name: 'Raven Watchtower',
    code: 'Agent RCB04',
    align: 'left',
    get title() {
      return t('landing.searchingGlobalWatchlists');
    },
    get caption() {
      return t('copy.watchlistCaption');
    },
    get signals() {
      return [t('copy.ofacMatch'), t('copy.interpolNotice'), t('copy.euSanctions')];
    },
    icon: 'shield',
  },
];

const ICONS = {
  chart: `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 17V7M8 17V3M13 17V10M18 17V5"/></svg>`,
  globe: `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="10" cy="10" r="7.5"/><path d="M2.5 10h15M10 2.5c2.5 2.8 2.5 12.2 0 15M10 2.5c-2.5 2.8-2.5 12.2 0 15"/></svg>`,
  search: `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="9" cy="9" r="5.5"/><path d="M13.5 13.5L17 17"/></svg>`,
  user: `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="10" cy="7" r="3.5"/><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg>`,
  shield: `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 2.5l6.5 2.5v5c0 4-2.8 7.5-6.5 8.5C6.3 17.5 3.5 14 3.5 10V5L10 2.5z"/></svg>`,
} as const;

// Scoped, light-only styles for the experience. Everything is nested under
// `.aw-root` so it cannot leak into the rest of the landing page.
const SCOPED_CSS = `
.aw-root {
  --aw-text: #0C1B4D;
  --aw-text-muted: #4A5A7A;
  --aw-tag-bg: #F3EFCE;
  --aw-tag-text: #5C4A12;
  --aw-spiral: #8B8BC0;
  --aw-title-grad-a: #7B6284;
  --aw-title-grad-b: #1B3098;
  --aw-caption-color: #604C67;
  --aw-signal-yellow: #8C7A2B;
  --aw-signal-green: #2E6B52;
  --aw-signal-red: #8A3E3E;
  --aw-font-header: "Libre Baskerville", Georgia, serif;
  --aw-font-body: "Montserrat", system-ui, sans-serif;
  position: relative;
}
.aw-scroll-container { position: relative; }
/* Pinning is driven by JS (see updatePin) rather than CSS sticky, because the
   landing scroll container's overflow makes it an ineffective sticky context. */
.aw-pin-sticky {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.aw-section-header {
  padding: 120px 200px 8px;
  flex-shrink: 0;
  will-change: opacity, transform;
}
.aw-section-tag {
  display: inline-block;
  background: var(--aw-tag-bg);
  color: var(--aw-tag-text);
  font-family: var(--aw-font-body);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 32px;
  margin-bottom: 20px;
  opacity: 0;
  transform: translateY(12px);
}
.aw-section-tag.visible { animation: awFadeUp 0.7s ease forwards; }
.aw-section-title {
  font-family: var(--aw-font-header);
  font-size: clamp(40px, 5vw, 60px);
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
  background: linear-gradient(50deg, var(--aw-title-grad-a) 0%, var(--aw-title-grad-b) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0;
  transform: translateY(16px);
}
.aw-section-title.visible { animation: awFadeUp 0.7s ease 0.1s forwards; }
.aw-section-caption {
  font-size: 14px;
  font-weight: 500;
  line-height: 2;
  color: var(--aw-caption-color);
  max-width: 680px;
  opacity: 0;
  transform: translateY(16px);
}
.aw-section-caption.visible { animation: awFadeUp 0.7s ease 0.2s forwards; }
@keyframes awFadeUp { to { opacity: 1; transform: translateY(0); } }
.aw-stage { flex: 1; position: relative; min-height: 0; margin-top: -180px; }
.aw-particles { position: absolute; inset: 0; display: block; pointer-events: none; z-index: 0; }
.aw-webgl { position: absolute; inset: 0; display: block; pointer-events: none; z-index: 1; }
.aw-labels-layer { position: absolute; inset: 0; pointer-events: none; z-index: 2; }
.aw-details-layer { position: absolute; inset: 0; pointer-events: none; z-index: 3; }
.aw-root .agent-label {
  position: absolute;
  text-align: center;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 2;
}
.aw-root .agent-label.visible { opacity: 1; }
.aw-root .agent-label .name {
  font-family: var(--aw-font-header);
  font-size: clamp(16px, 1.1vw, 20px);
  font-weight: 700;
  color: var(--aw-text);
  line-height: 1.3;
}
.aw-root .agent-label .code {
  font-family: var(--aw-font-body);
  font-size: clamp(8px, 0.9vw, 10px);
  font-weight: 500;
  color: var(--aw-text-muted);
  margin-top: 2px;
}
.aw-root .detail-card {
  position: absolute;
  width: clamp(500px, 28vw, 640px);
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.5s ease, transform 0.5s ease;
  pointer-events: auto;
}
.aw-root .detail-card.visible { transform: translateY(0); }
.aw-root .detail-card .card-title {
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: var(--aw-font-header);
  font-size: clamp(28px, 1.4vw, 40px);
  font-weight: 700;
  color: var(--aw-text);
  margin-bottom: 16px;
}
.aw-root .detail-card .card-title svg { flex-shrink: 0; width: 32px; height: 32px; color: var(--aw-spiral); }
.aw-root .detail-card .card-caption {
  font-size: clamp(14px, 1.1vw, 16px);
  font-weight: 500;
  line-height: 1.55;
  color: var(--aw-text-muted);
  margin-bottom: 14px;
}
.aw-root .live-signals-label {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--aw-text-muted);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.aw-root .live-signals-label::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #E0D252;
  box-shadow: 0 0 6px rgba(224, 210, 82, 0.6), 0 0 14px rgba(224, 210, 82, 0.35);
  animation: awLiveBeacon 2.8s ease-in-out infinite;
}
@keyframes awLiveBeacon {
  0% { transform: scale(0.9); background: #E0D252; opacity: 0.75; box-shadow: 0 0 4px rgba(224, 210, 82, 0.4), 0 0 10px rgba(224, 210, 82, 0.2); }
  25% { transform: scale(1.5); background: #57D287; opacity: 1; box-shadow: 0 0 10px rgba(87, 210, 135, 0.7), 0 0 24px rgba(87, 210, 135, 0.45); }
  50% { transform: scale(1); background: #FF6B6B; opacity: 0.85; box-shadow: 0 0 8px rgba(255, 107, 107, 0.6), 0 0 18px rgba(255, 107, 107, 0.35); }
  75% { transform: scale(1.35); background: #57D287; opacity: 1; box-shadow: 0 0 12px rgba(87, 210, 135, 0.7), 0 0 28px rgba(87, 210, 135, 0.45); }
  100% { transform: scale(0.9); background: #E0D252; opacity: 0.75; box-shadow: 0 0 4px rgba(224, 210, 82, 0.4), 0 0 10px rgba(224, 210, 82, 0.2); }
}
.aw-root .live-signals-stack { display: flex; flex-direction: column; min-height: 72px; }
.aw-root .live-signal {
  font-size: clamp(13px, 1.1vw, 13px);
  color: var(--aw-text);
  opacity: 0;
  transform: translateY(10px);
  max-height: 0;
  margin-bottom: 0;
  overflow: hidden;
  transition: opacity 0.45s ease, transform 0.45s ease,
    max-height 0.65s cubic-bezier(0.22, 1, 0.36, 1),
    margin-bottom 0.65s cubic-bezier(0.22, 1, 0.36, 1);
}
.aw-root .live-signal.visible { opacity: 1; transform: translateY(0); max-height: 40px; margin-bottom: 8px; }
.aw-root .live-signal.removing { opacity: 0; transform: translateY(-6px); max-height: 0; margin-bottom: 0; }
.aw-root .live-signal.signal-yellow { color: var(--aw-signal-yellow); }
.aw-root .live-signal.signal-green { color: var(--aw-signal-green); }
.aw-root .live-signal.signal-red { color: var(--aw-signal-red); }
.aw-scroll-hint {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--aw-text-muted);
  opacity: 0;
  transition: opacity 0.6s ease;
  z-index: 20;
  pointer-events: none;
}
.aw-scroll-hint.visible { opacity: 0.6; animation: awBounce 2.5s ease infinite; }
.aw-scroll-hint.hidden { opacity: 0 !important; animation: none; }
@keyframes awBounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(6px); }
}
@media (max-width: 900px) {
  .aw-section-header { padding: 96px 24px 8px; }
}
`;

export const AgentsAnimationSection = ({ className }: { className?: string }) => {
  useLocale();

  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // ── Scoped element lookups ──────────────────────────────────────
    // These always exist in the JSX below; assert non-null so the type
    // narrows through the many nested (hoisted) helper functions.
    const scrollContainer = root.querySelector<HTMLElement>('.aw-scroll-container')!;
    const pinSticky = root.querySelector<HTMLElement>('.aw-pin-sticky')!;
    const sectionHeader = root.querySelector<HTMLElement>('.aw-section-header')!;
    const scrollHint = root.querySelector<HTMLElement>('.aw-scroll-hint')!;
    const labelsLayer = root.querySelector<HTMLElement>('.aw-labels-layer')!;
    const detailsLayer = root.querySelector<HTMLElement>('.aw-details-layer')!;
    const canvas = root.querySelector<HTMLCanvasElement>('.aw-webgl')!;
    const pCanvas = root.querySelector<HTMLCanvasElement>('.aw-particles')!;

    // Disposal flag + tracked handles so every async loop/timer can bail out.
    let disposed = false;
    const rafIds = new Set<number>();
    const timeouts = new Set<ReturnType<typeof setTimeout>>();
    const raf = (cb: FrameRequestCallback) => {
      const id = requestAnimationFrame((t) => {
        rafIds.delete(id);
        if (!disposed) cb(t);
      });
      rafIds.add(id);
      return id;
    };
    const delay = (cb: () => void, ms: number) => {
      const id = setTimeout(() => {
        timeouts.delete(id);
        if (!disposed) cb();
      }, ms);
      timeouts.add(id);
      return id;
    };

    let revealed = false;
    let animationDistancePx = 0;
    const particleResizers: (() => void)[] = [];

    // In-bounds index helpers — every call site here reads a position that is
    // guaranteed to exist by construction; these keep the imperative code
    // readable under `noUncheckedIndexedAccess` without scattering assertions.
    function at<T>(arr: ArrayLike<T>, i: number): T {
      const v = arr[i];
      if (v === undefined) throw new Error(`index ${i} out of range`);
      return v;
    }
    function pick<T>(arr: readonly T[]): T {
      return at(arr, Math.floor(Math.random() * arr.length));
    }

    // ── Build label + detail DOM ────────────────────────────────────
    const labelEls = AGENTS.map((agent) => {
      const el = document.createElement('div');
      el.className = 'agent-label';
      el.innerHTML = `<div class="name">${agent.name}</div><div class="code">${agent.code}</div>`;
      labelsLayer.appendChild(el);
      return el;
    });

    const detailEls = AGENTS.map((agent, i) => {
      const el = document.createElement('div');
      el.className = 'detail-card';
      el.innerHTML = `
        <div class="card-title">${ICONS[agent.icon]}<span>${agent.title}</span></div>
        <p class="card-caption">${agent.caption}</p>
        <div class="live-signals-label">${t('common.liveSignals')}</div>
        <div class="live-signals-stack" data-index="${i}"></div>
      `;
      detailsLayer.appendChild(el);
      return el;
    });

    function startSignalCyclers() {
      detailEls.forEach((card, i) => {
        const stack = card.querySelector<HTMLElement>('.live-signals-stack')!;
        const signals = at(AGENTS, i).signals;
        const speed = 400 + Math.random() * 900;

        function spawnSignal() {
          if (Number.parseFloat(card.style.opacity || '0') < 0.5) {
            delay(spawnSignal, 1200);
            return;
          }

          const signal = document.createElement('div');
          const variants = ['signal-yellow', 'signal-green', 'signal-red'];
          const variant = pick(variants);
          signal.className = `live-signal ${variant}`;
          signal.textContent = pick(signals);
          stack.appendChild(signal);

          raf(() => signal.classList.add('visible'));

          const visibleDuration = 1200 + Math.random() * 1400;
          delay(() => {
            signal.classList.add('removing');
            delay(() => signal.remove(), 900);
          }, visibleDuration);

          const currentSignals = stack.querySelectorAll('.live-signal').length;
          let nextDelay = speed;
          if (currentSignals === 1) nextDelay *= 0.7;
          if (currentSignals === 2) nextDelay *= 1.15;
          if (currentSignals >= 3) nextDelay *= 1.8;
          delay(spawnSignal, nextDelay);
        }

        delay(spawnSignal, 600 + i * 700);
      });
    }

    // ── Reveal (comet intro removed) ────────────────────────────────
    // Reveal the header + all agent labels immediately so scrolling drives the
    // animation from the first frame instead of being locked behind an intro.
    function revealAll() {
      (['aw-section-tag', 'aw-section-title', 'aw-section-caption'] as const).forEach((cls) => {
        delay(() => root?.querySelector(`.${cls}`)?.classList.add('visible'), 200);
      });
      for (const el of labelEls) el.classList.add('visible');
      buildVerticalLayout(1);
      syncScrollTargets();
      scrollHint.classList.add('visible');
    }

    // ── Scroll (rect-driven, container-agnostic) ────────────────────
    let targetScroll = 0;
    let smoothScroll = 0;
    let targetPostT = 0;
    let smoothPostT = 0;

    function getScrolled() {
      // While the tall container scrolls past, its top goes 0 → negative.
      const rect = scrollContainer.getBoundingClientRect();
      return Math.max(0, -rect.top);
    }

    function getAnimationProgress() {
      return Math.min(1, Math.max(0, getScrolled() / Math.max(1, animationDistancePx)));
    }

    function getPostScrollT() {
      if (maxPostPanPx <= 0) return 0;
      const offset = getScrolled() - animationDistancePx;
      return Math.max(0, Math.min(1, offset / maxPostPanPx));
    }

    function updateScrollHeights() {
      animationDistancePx = window.innerHeight * (ANIMATION_SCROLL_VH / 100);
      scrollContainer.style.height = `${animationDistancePx + maxPostPanPx + window.innerHeight}px`;
    }

    function syncScrollTargets() {
      targetScroll = getAnimationProgress();
      targetPostT = getPostScrollT();
      if (targetScroll > 0.02 || targetPostT > 0.02) scrollHint.classList.add('hidden');
      else scrollHint.classList.remove('hidden');
    }

    // Manual pin: keep the stage fixed to the viewport while scrolling through the
    // tall spacer, then park it at the spacer's start/end. Replaces CSS sticky.
    function updatePin() {
      const total = animationDistancePx + maxPostPanPx;
      const scrolled = getScrolled();
      const s = pinSticky.style;
      const rect = scrollContainer.getBoundingClientRect();
      if (scrolled <= 0) {
        s.position = 'absolute';
        s.top = '0px';
        s.left = '0';
        s.width = '100%';
      } else if (scrolled >= total) {
        s.position = 'absolute';
        s.top = `${total}px`;
        s.left = '0';
        s.width = '100%';
      } else {
        s.position = 'fixed';
        s.top = '0px';
        s.left = `${rect.left}px`;
        s.width = `${rect.width}px`;
      }
    }

    // Nearest scrollable ancestor (the landing scroll container) or window.
    const scrollParent = root.closest<HTMLElement>('.landing-scroll-container');
    const scrollSource: HTMLElement | Window = scrollParent ?? window;

    function onScroll() {
      if (!revealed) return;
      syncScrollTargets();
    }
    scrollSource.addEventListener('scroll', onScroll, { passive: true });

    function smoothstep(t: number) {
      return t * t * (3 - 2 * t);
    }
    function easeInOut(t: number) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }
    function phaseProgress(p: number, startV: number, endV: number) {
      return smoothstep(Math.max(0, Math.min(1, (p - startV) / (endV - startV))));
    }

    // ── Layout constants ────────────────────────────────────────────
    const ROW_GAP_PX = 130;
    const TEXT_GAP_PX = 72;
    const ANIMATION_SCROLL_VH = 320;
    const POINTS_PER = 3000;
    const RADIUS = 4;
    const MAX_COILS = 3.5;
    const PITCH = 0.8;
    const H_SPACING = 9;
    const MARGIN_X = 5.5;

    // ── Three.js ────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      45,
      pinSticky.clientWidth / pinSticky.clientHeight,
      0.1,
      300,
    );
    camera.position.set(0, 12, 0.001);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(pinSticky.clientWidth, pinSticky.clientHeight);
    renderer.setClearColor(0x000000, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const point = new THREE.PointLight(0xffffff, 2);
    point.position.set(8, 10, 10);
    scene.add(point);

    const baseColor = new THREE.Color('#8B8BC0');
    const movingGlow = new THREE.Color('#E0D252');

    const hPositions = AGENTS.map((_, i) => ({ x: (i - 2) * H_SPACING, y: 0, z: 0 }));

    let vPositions: { x: number; y: number; z: number }[] = [];
    let maxPostPanPx = 0;
    let anchorPan = 0;
    let browsePan = 0;
    const STAGE_OFFSET_FINAL = 0.6;

    const spirals = AGENTS.map((_, idx) => {
      const positions = new Float32Array(POINTS_PER * 3);
      const colors = new Float32Array(POINTS_PER * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      const material = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 1,
      });
      const line = new THREE.Line(geometry, material);
      scene.add(line);
      return { line, geometry, positions, colors, index: idx };
    });

    let currentRadius = RADIUS;

    const camTop = new THREE.Vector3(0, 34, 0.001);
    const camPerspective = new THREE.Vector3(3, 8, 16);
    const camSeparated = new THREE.Vector3(0, 4, 26);
    const lookTop = new THREE.Vector3(0, 0, 0);
    const lookPerspective = new THREE.Vector3(0, 1, 0);
    const lookSeparated = new THREE.Vector3(0, 0, 0);

    const _proj = new THREE.Vector3();
    const _color = new THREE.Color();

    function projectToScreen(x: number, y: number, z: number) {
      _proj.set(x, y, z);
      _proj.project(camera);
      return {
        x: (_proj.x * 0.5 + 0.5) * pinSticky.clientWidth,
        y: (-_proj.y * 0.5 + 0.5) * pinSticky.clientHeight,
      };
    }

    function getPixelsPerWorldUnit(refY = 0) {
      const a = projectToScreen(0, refY, 0);
      const b = projectToScreen(0, refY + 1, 0);
      return Math.abs(b.y - a.y) || 1;
    }

    function getHelixHeight(scrollFactor: number) {
      const coils = scrollFactor * MAX_COILS;
      return scrollFactor * coils * Math.PI * PITCH;
    }

    function getSpiralScreenHeight(scrollFactor: number, worldY: number) {
      const helixH = getHelixHeight(scrollFactor);
      const layoutRadius = RADIUS * (1.0 + 0.3 * easeInOut(scrollFactor));
      const top = projectToScreen(0, worldY + helixH + layoutRadius * 0.3, 0);
      const bottom = projectToScreen(0, worldY - layoutRadius * 0.3, 0);
      return Math.abs(bottom.y - top.y);
    }

    function getStageMetrics() {
      const stageEl = root?.querySelector<HTMLElement>('.aw-stage');
      const rect =
        stageEl?.getBoundingClientRect() ?? ({ top: 0, bottom: 0, left: 0, width: 0 } as DOMRect);
      return {
        top: rect.top + 48,
        bottom: rect.bottom - 32,
        centerX: rect.left + rect.width * 0.5,
      };
    }

    function worldPanForScreenDelta(deltaScreenPx: number, refWorldY: number) {
      const base = projectToScreen(0, refWorldY, 0);
      const shifted = projectToScreen(0, refWorldY + 1, 0);
      const screenPerWorld = shifted.y - base.y;
      if (Math.abs(screenPerWorld) < 0.001) return 0;
      return -deltaScreenPx / screenPerWorld;
    }

    function rowPanAtIndex(rowIndex: number, panWorld: number, scrollFactor = 1) {
      const vp = at(vPositions, rowIndex);
      if (!vp) return 0;
      const wy = vp.y - STAGE_OFFSET_FINAL - panWorld;
      return getSpiralCenterY(wy, scrollFactor);
    }

    function projectRowCenter(rowIndex: number, panWorld: number, scrollFactor = 1) {
      const vp = at(vPositions, rowIndex);
      const centerY = rowPanAtIndex(rowIndex, panWorld, scrollFactor);
      return projectToScreen(vp.x, centerY, 0);
    }

    function computeBrowsePans(scrollFactor = 1) {
      const stage = getStageMetrics();
      camera.position.copy(camSeparated);
      camera.lookAt(lookSeparated);
      camera.updateMatrixWorld();

      const firstTargetY = stage.top + 700;
      const s0 = projectRowCenter(0, 0, scrollFactor);
      anchorPan = worldPanForScreenDelta(firstTargetY - s0.y, rowPanAtIndex(0, 0, scrollFactor));

      const lastIdx = AGENTS.length - 1;
      const rowWorldSpan = at(vPositions, 0).y - at(vPositions, lastIdx).y;
      const sLastAnchored = projectRowCenter(lastIdx, anchorPan, scrollFactor);
      const lastTargetY = stage.bottom - 80;

      browsePan = worldPanForScreenDelta(
        lastTargetY - sLastAnchored.y,
        rowPanAtIndex(lastIdx, anchorPan, scrollFactor),
      );

      if (Math.abs(browsePan) < rowWorldSpan * 0.15) browsePan = rowWorldSpan * 0.95;

      const pxPerUnit = getPixelsPerWorldUnit(rowPanAtIndex(0, anchorPan, scrollFactor));
      const browsePx = Math.abs(browsePan * pxPerUnit);
      maxPostPanPx = Math.max(browsePx, window.innerHeight * (AGENTS.length - 1) * 0.55);
      updateScrollHeights();
    }

    function buildVerticalLayout(scrollFactor = 1) {
      const savedPos = camera.position.clone();
      const savedQuat = camera.quaternion.clone();

      camera.position.copy(camSeparated);
      camera.lookAt(lookSeparated);
      camera.updateMatrixWorld();

      const pxPerUnit = getPixelsPerWorldUnit(0);
      const rowGapWorld = ROW_GAP_PX / pxPerUnit;
      const helixWorldH = getHelixHeight(scrollFactor) + RADIUS * 2;
      const rowStep = helixWorldH + rowGapWorld;
      const firstRowY = 8.5;

      vPositions = AGENTS.map((agent, i) => ({
        x: agent.align === 'left' ? -MARGIN_X : MARGIN_X,
        y: firstRowY - i * rowStep,
        z: 0,
      }));

      lookSeparated.set(0, firstRowY - rowStep * 1.2, 0);
      computeBrowsePans(scrollFactor);

      camera.position.copy(savedPos);
      camera.quaternion.copy(savedQuat);
    }

    function updateSpiralGeometry(
      spiral: (typeof spirals)[number],
      worldX: number,
      worldY: number,
      worldZ: number,
      scrollFactor: number,
      time: number,
      phaseOffset: number,
      helixRadius: number,
    ) {
      const { positions, colors } = spiral;
      const coils = scrollFactor * MAX_COILS;
      const movingHead = (time * 0.12 + phaseOffset) % 1;

      for (let i = 0; i < POINTS_PER; i++) {
        const t = i / (POINTS_PER - 1);
        const angle = t * Math.PI * 2 * (1 + coils);
        const x = Math.cos(angle) * helixRadius;
        const z = Math.sin(angle) * helixRadius;
        const y = scrollFactor * t * coils * Math.PI * PITCH;

        const i3 = i * 3;
        positions[i3] = x + worldX;
        positions[i3 + 1] = y + worldY;
        positions[i3 + 2] = z + worldZ;

        let dist = Math.abs(t - movingHead);
        dist = Math.min(dist, 1 - dist);
        const glowStrength = Math.max(0, 1 - dist * 30);
        _color.copy(baseColor).lerp(movingGlow, glowStrength);
        const brightness = 0.88 + glowStrength * 1.4;
        colors[i3] = _color.r * brightness;
        colors[i3 + 1] = _color.g * brightness;
        colors[i3 + 2] = _color.b * brightness;
      }

      spiral.geometry.getAttribute('position').needsUpdate = true;
      spiral.geometry.getAttribute('color').needsUpdate = true;
    }

    function getSpiralCenterY(worldY: number, scrollFactor: number) {
      return worldY + getHelixHeight(scrollFactor) * 0.5;
    }

    const clock = new THREE.Clock();

    let _particleSepT = 0;
    let _particlePanWorld = 0;
    let _particleScrollF = 0;
    let _particleStageOffY = 0;

    function animate() {
      raf(animate);
      const time = clock.getElapsedTime();

      // Recompute scroll targets from the section's rect every frame. This keeps
      // the experience driven correctly regardless of whether the page scrolls on
      // the window, the landing container, or any other ancestor.
      updatePin();
      if (revealed) syncScrollTargets();

      smoothScroll += (targetScroll - smoothScroll) * 0.07;
      smoothPostT += (targetPostT - smoothPostT) * 0.07;
      const p = revealed ? Math.min(1, smoothScroll) : 0;

      const perspectiveT = phaseProgress(p, 0, 0.32);
      const separateT = phaseProgress(p, 0.28, 0.62);
      const detailsT = phaseProgress(p, 0.58, 1);

      const headerFade = 1 - phaseProgress(p, 0.15, 0.35);
      const headerShift = -phaseProgress(p, 0.15, 0.38) * 60;
      sectionHeader.style.opacity = String(headerFade);
      sectionHeader.style.transform = `translateY(${headerShift}px)`;

      const scrollFactor = p;
      const scaledRadius = RADIUS * (1.0 + 0.3 * easeInOut(p));
      currentRadius = scaledRadius;

      const camPos = new THREE.Vector3().lerpVectors(camTop, camPerspective, perspectiveT);
      if (separateT > 0) camPos.lerp(camSeparated, separateT);
      camera.position.copy(camPos);

      const lookTarget = new THREE.Vector3().lerpVectors(lookTop, lookPerspective, perspectiveT);
      if (separateT > 0) lookTarget.lerp(lookSeparated, separateT);
      camera.lookAt(lookTarget);

      const stageOffsetY = separateT * STAGE_OFFSET_FINAL;
      const framingT = phaseProgress(p, 0.58, 1);
      const animPan = framingT * anchorPan;
      const browseOffset = smoothPostT * browsePan;
      const panWorld = animPan + browseOffset;

      _particleSepT = separateT;
      _particlePanWorld = panWorld;
      _particleScrollF = scrollFactor;
      _particleStageOffY = stageOffsetY;

      spirals.forEach((spiral, i) => {
        const hp = at(hPositions, i);
        const vp = at(vPositions, i) || hp;
        const wx = hp.x + ((vp.x ?? hp.x) - hp.x) * separateT;
        const wy = hp.y + ((vp.y ?? hp.y) - hp.y) * separateT - stageOffsetY - panWorld;
        const wz = hp.z + ((vp.z ?? hp.z) - hp.z) * separateT;
        updateSpiralGeometry(spiral, wx, wy, wz, scrollFactor, time, i * 0.17, currentRadius);
        spiral.line.material.opacity = 1;
      });

      labelEls.forEach((el, i) => {
        const hp = at(hPositions, i);
        const vp = at(vPositions, i) || hp;
        const wx = hp.x + ((vp.x ?? hp.x) - hp.x) * separateT;
        const wy = hp.y + ((vp.y ?? hp.y) - hp.y) * separateT - stageOffsetY - panWorld;
        const centerY = getSpiralCenterY(wy, scrollFactor);
        const screen = projectToScreen(wx, centerY, 0);
        el.style.left = `${screen.x}px`;
        el.style.top = `${screen.y}px`;
        el.style.transform = 'translate(-50%, -50%)';
        el.style.opacity = el.classList.contains('visible') ? '1' : '0';
      });

      detailEls.forEach((el, i) => {
        const agent = at(AGENTS, i);
        const hp = at(hPositions, i);
        const vp = at(vPositions, i) || hp;
        const wx = hp.x + ((vp.x ?? hp.x) - hp.x) * separateT;
        const wy = hp.y + ((vp.y ?? hp.y) - hp.y) * separateT - stageOffsetY - panWorld;
        const centerY = getSpiralCenterY(wy, scrollFactor);

        const leftEdge = projectToScreen(wx - currentRadius, centerY, 0);
        const rightEdge = projectToScreen(wx + currentRadius, centerY, 0);
        const centerScreen = projectToScreen(wx, centerY, 0);

        const stagger = i * 0.08;
        const cardT = phaseProgress(detailsT, stagger, stagger + 0.3);
        el.classList.toggle('visible', cardT > 0.45);
        el.style.opacity = String(cardT);

        const cardW = el.offsetWidth || 200;
        const cardH = el.offsetHeight || 160;
        const cardX =
          agent.align === 'left' ? rightEdge.x + TEXT_GAP_PX : leftEdge.x - TEXT_GAP_PX - cardW;
        const cardY = centerScreen.y - cardH * 0.45;
        el.style.left = `${cardX}px`;
        el.style.top = `${cardY}px`;
      });

      renderer.render(scene, camera);
    }

    function onResize() {
      const animP = getAnimationProgress();
      const postT = getPostScrollT();
      camera.aspect = pinSticky.clientWidth / pinSticky.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(pinSticky.clientWidth, pinSticky.clientHeight);
      buildVerticalLayout(1);
      syncScrollTargets();
      void animP;
      void postT;
    }
    window.addEventListener('resize', onResize);

    buildVerticalLayout(1);
    startSignalCyclers();
    animate();

    // ── Particle system ─────────────────────────────────────────────
    const ctx = pCanvas.getContext('2d')!;
    {
      const initParticles = () => {
        function resize() {
          pCanvas.width = pinSticky.clientWidth;
          pCanvas.height = pinSticky.clientHeight;
        }
        resize();
        window.addEventListener('resize', resize);
        particleResizers.push(resize);

        const FLOAT_COLS = [
          { r: 172, g: 172, b: 210 },
          { r: 106, g: 106, b: 175 },
          { r: 196, g: 182, b: 201 },
          { r: 225, g: 229, b: 250 },
        ];
        const EXIT_COLS = [
          { r: 214, g: 169, b: 186 },
          { r: 217, g: 201, b: 166 },
          { r: 134, g: 197, b: 184 },
        ];

        const PARTICLES_PER_SPIRAL = 60;
        const BASE_R = 2.8;

        function helixWorldPoint(
          spiralIdx: number,
          t: number,
          helixPhase: number,
          innerFrac: number,
        ) {
          const hp = at(hPositions, spiralIdx);
          const vp = at(vPositions, spiralIdx) || hp;
          const sep = _particleSepT;
          const pan = _particlePanWorld;
          const sf = _particleScrollF;
          const soY = _particleStageOffY;

          const wx = hp.x + ((vp.x ?? hp.x) - hp.x) * sep;
          const wy = hp.y + ((vp.y ?? hp.y) - hp.y) * sep - soY - pan;
          const wz = hp.z + ((vp.z ?? hp.z) - hp.z) * sep;

          const coils = sf * MAX_COILS;
          const angle = t * Math.PI * 2 * (1 + coils) + helixPhase;
          const r = currentRadius * innerFrac;
          const x3 = Math.cos(angle) * r + wx;
          const z3 = Math.sin(angle) * r + wz;
          const y3 = sf * t * coils * Math.PI * PITCH + wy;
          return { x: x3, y: y3, z: z3, wx, wy, wz, sf, coils };
        }

        function worldToScreen(x: number, y: number, z: number) {
          _proj.set(x, y, z);
          _proj.project(camera);
          return {
            x: (_proj.x * 0.5 + 0.5) * pinSticky.clientWidth,
            y: (-_proj.y * 0.5 + 0.5) * pinSticky.clientHeight,
          };
        }

        function spiralCenterBottom(spiralIdx: number) {
          const hp = at(hPositions, spiralIdx);
          const vp = at(vPositions, spiralIdx) || hp;
          const sep = _particleSepT;
          const pan = _particlePanWorld;
          const soY = _particleStageOffY;
          const wx = hp.x + ((vp.x ?? hp.x) - hp.x) * sep;
          const wy = hp.y + ((vp.y ?? hp.y) - hp.y) * sep - soY - pan;
          const wz = hp.z + ((vp.z ?? hp.z) - hp.z) * sep;
          return worldToScreen(wx, wy, wz);
        }

        function spiralMidScreen(spiralIdx: number) {
          const hp = at(hPositions, spiralIdx);
          const vp = at(vPositions, spiralIdx) || hp;
          const sep = _particleSepT;
          const pan = _particlePanWorld;
          const sf = _particleScrollF;
          const soY = _particleStageOffY;
          const wx = hp.x + ((vp.x ?? hp.x) - hp.x) * sep;
          const wy = hp.y + ((vp.y ?? hp.y) - hp.y) * sep - soY - pan;
          const wz = hp.z + ((vp.z ?? hp.z) - hp.z) * sep;
          const helixH = getHelixHeight(sf);
          return worldToScreen(wx, wy + helixH * 0.5, wz);
        }

        type Dot = ReturnType<typeof makeDot>;
        function makeDot(offscreen: boolean) {
          const col = pick(FLOAT_COLS);
          const angle = Math.random() * Math.PI * 2;
          const spd = 0.05 + Math.random() * 0.15;
          const d = {
            col,
            r: BASE_R * (0.5 + Math.random() * 0.95),
            alpha: 0.18 + Math.random() * 0.28,
            vx: Math.cos(angle) * spd,
            vy: Math.sin(angle) * spd,
            tremblePhaseX: Math.random() * Math.PI * 2,
            tremblePhaseY: Math.random() * Math.PI * 2,
            trembleAmpX: 0.15 + Math.random() * 0.2,
            trembleAmpY: 0.15 + Math.random() * 0.2,
            trembleFreqX: 0.016 + Math.random() * 0.018,
            trembleFreqY: 0.016 + Math.random() * 0.018,
            state: 'float' as 'float' | 'pull' | 'spiral' | 'exit',
            spiralIdx: -1,
            spiralT: 0,
            spiralSpeed: 0,
            helixPhase: Math.random() * Math.PI * 2,
            innerFrac: 0.35 + Math.random() * 0.45,
            exitCol: null as { r: number; g: number; b: number } | null,
            exitVx: 0,
            exitVy: 0,
            exitLife: 1,
            starSize: BASE_R,
            exitWorldX: 0,
            exitWorldY: 0,
            exitWorldZ: 0,
            x: 0,
            y: 0,
          };

          if (offscreen) {
            const edge = Math.floor(Math.random() * 4);
            if (edge === 0) {
              d.x = -12;
              d.y = Math.random() * pinSticky.clientHeight;
            } else if (edge === 1) {
              d.x = pinSticky.clientWidth + 12;
              d.y = Math.random() * pinSticky.clientHeight;
            } else if (edge === 2) {
              d.x = Math.random() * pinSticky.clientWidth;
              d.y = -12;
            } else {
              d.x = Math.random() * pinSticky.clientWidth;
              d.y = pinSticky.clientHeight + 12;
            }
            const cx = pinSticky.clientWidth / 2;
            const cy = pinSticky.clientHeight / 2;
            const dx = cx - d.x;
            const dy = cy - d.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            d.vx = (dx / len) * spd;
            d.vy = (dy / len) * spd;
          } else {
            d.x = Math.random() * pinSticky.clientWidth;
            d.y = Math.random() * pinSticky.clientHeight;
          }
          return d;
        }

        const particlesBySpiral: Dot[][] = AGENTS.map(() => []);
        for (let s = 0; s < AGENTS.length; s++) {
          for (let i = 0; i < PARTICLES_PER_SPIRAL; i++) {
            const d = makeDot(true);
            d.state = 'spiral';
            d.spiralIdx = s;
            d.spiralT = Math.random();
            d.spiralSpeed = 0.00035 + Math.random() * 0.0002;
            at(particlesBySpiral, s).push(d);
          }
        }

        function pullAllowed() {
          return revealed && targetScroll > 0.28;
        }
        function spiralActive(i: number) {
          return at(spirals, i).line.material.opacity > 0.15;
        }
        function spiralOnScreen(mid: { x: number; y: number }) {
          return (
            mid.x > -300 &&
            mid.x < pinSticky.clientWidth + 300 &&
            mid.y > -300 &&
            mid.y < pinSticky.clientHeight + 300
          );
        }

        let lastTime = performance.now();

        function tickParticles() {
          raf(tickParticles);
          const now = performance.now();
          const dt = Math.min((now - lastTime) / 16.67, 2.5);
          lastTime = now;

          ctx.clearRect(0, 0, pCanvas.width, pCanvas.height);
          const canPull = pullAllowed();
          const spiralMids = canPull ? spirals.map((_, i) => spiralMidScreen(i)) : [];
          const active = canPull
            ? spirals.reduce<number[]>((acc, _, i) => {
                if (spiralActive(i) && spiralOnScreen(at(spiralMids, i))) acc.push(i);
                return acc;
              }, [])
            : [];

          const particleFade = Math.max(0, Math.min(1, (_particleScrollF - 0.02) / 0.2));

          particlesBySpiral.forEach((spiralParticles) => {
            spiralParticles.forEach((d) => {
              if (_particleScrollF < 0.15) {
                ctx.clearRect(0, 0, pCanvas.width, pCanvas.height);
                return;
              }

              if (d.state === 'float') {
                d.tremblePhaseX += d.trembleFreqX * dt;
                d.tremblePhaseY += d.trembleFreqY * dt;
                d.x += (d.vx + Math.sin(d.tremblePhaseX) * d.trembleAmpX) * dt;
                d.y += (d.vy + Math.cos(d.tremblePhaseY) * d.trembleAmpY) * dt;

                if (d.x < -15) d.x = pinSticky.clientWidth + 5;
                if (d.x > pinSticky.clientWidth + 15) d.x = -5;
                if (d.y < -15) d.y = pinSticky.clientHeight + 5;
                if (d.y > pinSticky.clientHeight + 15) d.y = -5;

                if (Math.random() < 0.0007 * dt) {
                  const a = Math.random() * Math.PI * 2;
                  const s = 0.05 + Math.random() * 0.15;
                  d.vx = Math.cos(a) * s;
                  d.vy = Math.sin(a) * s;
                }

                if (active.length > 0) {
                  let near = -1;
                  let nearD = Infinity;
                  active.forEach((i) => {
                    const m = at(spiralMids, i);
                    const dx = m.x - d.x;
                    const dy = m.y - d.y;
                    const dd = Math.sqrt(dx * dx + dy * dy);
                    if (dd < nearD) {
                      nearD = dd;
                      near = i;
                    }
                  });
                  const pullR = Math.min(pinSticky.clientWidth, pinSticky.clientHeight) * 1.2;
                  if (nearD < pullR) {
                    d.state = 'pull';
                    d.spiralIdx = near;
                  }
                }

                const { r, g, b } = d.col;
                ctx.beginPath();
                ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${r},${g},${b},${d.alpha.toFixed(2)})`;
                ctx.fill();
                return;
              }

              if (d.state === 'pull') {
                if (!spiralActive(d.spiralIdx)) {
                  d.state = 'float';
                  return;
                }
                const base = spiralCenterBottom(d.spiralIdx);
                const dx = base.x - d.x;
                const dy = base.y - d.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const str = 0.02 + (1 - Math.min(1, dist / 380)) * 0.03;
                d.vx = d.vx * 0.93 + dx * str * dt;
                d.vy = d.vy * 0.93 + dy * str * dt;
                d.x += d.vx * dt;
                d.y += d.vy * dt;

                if (dist < 22) {
                  d.state = 'spiral';
                  d.spiralT = 0;
                  d.spiralSpeed = 0.00035 + Math.random() * 0.0002;
                }

                const { r, g, b } = d.col;
                ctx.beginPath();
                ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${r},${g},${b},${(d.alpha * 0.9 * particleFade).toFixed(2)})`;
                ctx.shadowColor = `rgba(${r},${g},${b},0.18)`;
                ctx.shadowBlur = 5;
                ctx.fill();
                ctx.shadowBlur = 0;
                return;
              }

              if (d.state === 'spiral') {
                if (!spiralActive(d.spiralIdx)) {
                  d.state = 'float';
                  return;
                }
                d.spiralT += d.spiralSpeed * dt;
                const oldX = d.x;
                const oldY = d.y;
                const wp = helixWorldPoint(d.spiralIdx, d.spiralT, d.helixPhase, d.innerFrac);
                const scr = worldToScreen(wp.x, wp.y, wp.z);
                d.x = scr.x;
                d.y = scr.y;

                if (d.spiralT >= 1) {
                  d.state = 'exit';
                  d.exitCol = pick(EXIT_COLS);
                  d.starSize = BASE_R * (1.4 + Math.random() * 0.7);
                  const vx = d.x - oldX;
                  const vy = d.y - oldY;
                  const len = Math.hypot(vx, vy);
                  const dirX = len > 0 ? vx / len : 0;
                  const dirY = len > 0 ? vy / len : -1;
                  const perpX = -dirY;
                  const perpY = dirX;
                  const spread = (Math.random() - 0.5) * 0.01;
                  const speed = len * (0.9 + Math.random() * 0.1);
                  d.exitVx = dirX * speed + perpX * spread;
                  d.exitVy = dirY * speed + perpY * spread;
                  d.exitLife = 1;
                  d.exitWorldX = wp.x;
                  d.exitWorldY = wp.y;
                  d.exitWorldZ = wp.z;
                  return;
                }

                const { r, g, b } = d.col;
                ctx.fillStyle = `rgba(${r},${g},${b},${(d.alpha * particleFade).toFixed(2)})`;
                ctx.shadowColor = `rgba(${r},${g},${b},0.9)`;
                ctx.shadowBlur = 12;
                ctx.fillRect(d.x - 1, d.y - 1, 2, 2);
                ctx.fillRect(d.x - 3, d.y, 6, 1);
                ctx.fillRect(d.x, d.y - 3, 1, 6);
                ctx.shadowBlur = 0;
                return;
              }

              if (d.state === 'exit') {
                d.exitVx *= 0.985;
                d.exitVy *= 0.985;
                d.exitWorldX += d.exitVx * 0.004 * dt;
                d.exitWorldY += d.exitVy * 0.004 * dt;
                const scrExit = worldToScreen(d.exitWorldX, d.exitWorldY, d.exitWorldZ);
                d.x = scrExit.x;
                d.y = scrExit.y;
                d.exitLife -= 0.015 * dt;

                if (d.exitLife <= 0) {
                  const oldSpiralIdx = d.spiralIdx;
                  Object.assign(d, makeDot(false));
                  d.spiralIdx = oldSpiralIdx;
                  d.state = 'spiral';
                  d.spiralT = Math.random() * 0.15;
                  d.spiralSpeed = 0.00035 + Math.random() * 0.0002;
                  d.helixPhase = Math.random() * Math.PI * 2;
                  d.innerFrac = 0.35 + Math.random() * 0.45;
                  return;
                }

                const a = Math.max(0, d.exitLife);
                const radius = d.starSize * (1 + (1 - a) * 0.45);
                const exitCol = d.exitCol ?? { r: 214, g: 169, b: 186 };
                ctx.fillStyle = `rgba(${exitCol.r},${exitCol.g},${exitCol.b},${(a * 0.8).toFixed(2)})`;
                ctx.shadowColor = `rgba(${exitCol.r},${exitCol.g},${exitCol.b},${(a * 0.25).toFixed(2)})`;
                ctx.shadowBlur = 10;
                const scale = radius / 3;
                ctx.fillRect(d.x - scale, d.y - scale, scale * 2, scale * 2);
                ctx.fillRect(d.x - scale * 3, d.y, scale * 6, 1);
                ctx.fillRect(d.x, d.y - scale * 3, 1, scale * 6);
                ctx.shadowBlur = 0;
              }
            });
          });
        }

        tickParticles();
      };

      initParticles();
    }

    // ── Reveal the first time the section scrolls into view ─────────
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !revealed) {
            revealed = true;
            revealAll();
            observer.disconnect();
          }
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(pinSticky);

    // ── Cleanup ─────────────────────────────────────────────────────
    return () => {
      disposed = true;
      observer.disconnect();
      for (const id of rafIds) cancelAnimationFrame(id);
      for (const id of timeouts) clearTimeout(id);
      scrollSource.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      for (const fn of particleResizers) window.removeEventListener('resize', fn);
      for (const s of spirals) {
        s.geometry.dispose();
        s.line.material.dispose();
      }
      renderer.dispose();
      for (const el of labelEls) el.remove();
      for (const el of detailEls) el.remove();
    };
  }, [getLocale()]);

  return (
    <section className={cn('aw-root w-full select-none', className)} ref={rootRef}>
      <style dangerouslySetInnerHTML={{ __html: SCOPED_CSS }} />
      <div className="aw-scroll-container">
        <div className="aw-pin-sticky">
          <header className="aw-section-header">
            <span className="aw-section-tag">{t('landing.agentDrivenIntelligence')}</span>
            <h2 className="aw-section-title">{t('landing.alwaysWatching')}</h2>
            <p className="aw-section-caption">
              {t('landing.behindTheScenesMultipleRavenAgentsOperateSimultaneouslyMonitoring')}{' '}
            </p>
          </header>

          <div className="aw-stage">
            <canvas className="aw-particles" />
            <canvas className="aw-webgl" />
            <div className="aw-labels-layer" />
            <div className="aw-details-layer" />
          </div>

          <div className="aw-scroll-hint">{t('landing.scrollToExplore')}</div>
        </div>
      </div>
    </section>
  );
};
